import { useState, useCallback } from "react";
import { Layout } from "@/components/Layout";
import { useLoadsQuery } from "@/hooks/useLoadsQuery";
import { useLoadsListMutations } from "@/hooks/useLoadsListMutations";
import { 
  LoadsHeader, 
  LoadsFilters, 
  LoadsList, 
  LoadStatusDialog 
} from "@/components/loads/list";
import { 
  LoadFilters, 
  DEFAULT_LOAD_FILTERS, 
  StatusDialogState,
  LoadListItem,
  LoadInstallation
} from "@/types/loads";
import { InstallationStatus, LoadStatus } from "@/config/constants";
import { getCustomerDisplayName } from "@/lib/formatters";
import { openPrintableRouteList } from "@/components/loads/PrintableRouteList";

export default function Loads() {
  // Filter state
  const [filters, setFilters] = useState<LoadFilters>(DEFAULT_LOAD_FILTERS);
  
  // UI state
  const [expandedLoads, setExpandedLoads] = useState<Set<string>>(new Set());
  const [expandedInstallations, setExpandedInstallations] = useState<Set<string>>(new Set());
  const [showMapForLoad, setShowMapForLoad] = useState<Set<string>>(new Set());
  const [highlightedTaskId, setHighlightedTaskId] = useState<string | null>(null);
  
  // Status dialog state
  const [statusDialog, setStatusDialog] = useState<StatusDialogState | null>(null);
  const [statusReason, setStatusReason] = useState("");

  // Data hooks
  const { 
    loads, 
    filteredLoads, 
    installers, 
    installationsMap, 
    isLoading, 
    hasActiveFilters 
  } = useLoadsQuery(filters);
  
  const { updateLoadStatus, updateInstallationStatus } = useLoadsListMutations();

  // Toggle handlers
  const toggleExpand = useCallback((loadId: string) => {
    setExpandedLoads(prev => {
      const next = new Set(prev);
      if (next.has(loadId)) {
        next.delete(loadId);
      } else {
        next.add(loadId);
      }
      return next;
    });
  }, []);

  const toggleInstallationExpand = useCallback((installationId: string) => {
    setExpandedInstallations(prev => {
      const next = new Set(prev);
      if (next.has(installationId)) {
        next.delete(installationId);
      } else {
        next.add(installationId);
      }
      return next;
    });
  }, []);

  const toggleMapForLoad = useCallback((loadId: string) => {
    setShowMapForLoad(prev => {
      const next = new Set(prev);
      if (next.has(loadId)) {
        next.delete(loadId);
      } else {
        next.add(loadId);
      }
      return next;
    });
  }, []);

  const handleTaskClick = useCallback((taskId: string) => {
    setHighlightedTaskId(taskId);
    const element = document.getElementById(`admin-task-${taskId}`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    setTimeout(() => setHighlightedTaskId(null), 2000);
  }, []);

  // Installation status change - open dialog for partial/skipped
  const handleInstallationStatusChange = useCallback((installationId: string, status: InstallationStatus) => {
    if (status === "partial" || status === "skipped") {
      setStatusDialog({ open: true, installationId, status });
      setStatusReason("");
    } else {
      updateInstallationStatus(installationId, status);
    }
  }, [updateInstallationStatus]);

  const handleStatusDialogConfirm = useCallback(() => {
    if (statusDialog) {
      updateInstallationStatus(
        statusDialog.installationId,
        statusDialog.status,
        statusReason || undefined
      );
      setStatusDialog(null);
      setStatusReason("");
    }
  }, [statusDialog, statusReason, updateInstallationStatus]);

  const handleStatusDialogClose = useCallback(() => {
    setStatusDialog(null);
    setStatusReason("");
  }, []);

  // Print handler
  const handlePrintLoad = useCallback((load: LoadListItem, installations: LoadInstallation[]) => {
    const installerName = load.installer 
      ? `${load.installer.first_name || ""} ${load.installer.last_name || ""}`.trim() || "Nimetön"
      : "Ei asentajaa";

    const routeItems = installations.map((inst, index) => ({
      position: inst.load_position || index + 1,
      orderNumber: inst.order?.order_number || "",
      customerName: getCustomerDisplayName(inst.order?.customer),
      cemeteryName: inst.cemetery?.name || "",
      cemeteryCity: inst.cemetery?.city || "",
      taskType: inst.task_type || "",
      lat: inst.latitude || inst.cemetery?.latitude || 0,
      lng: inst.longitude || inst.cemetery?.longitude || 0,
    }));

    openPrintableRouteList({
      loadDate: load.load_date,
      installerName,
      notes: load.notes || "",
      routeItems,
      totalDistanceKm: load.total_distance_km ?? undefined,
      estimatedDurationMin: load.estimated_duration_min ?? undefined,
      depotName: "Verstas - Kuntolantie 52, Kurikka",
    });
  }, []);

  // Clear filters
  const handleClearFilters = useCallback(() => {
    setFilters(DEFAULT_LOAD_FILTERS);
  }, []);

  return (
    <Layout>
      <div className="space-y-6">
        <LoadsHeader />
        
        <LoadsFilters
          filters={filters}
          onFiltersChange={setFilters}
          installers={installers}
          hasActiveFilters={hasActiveFilters}
        />

        <LoadsList
          loads={loads}
          filteredLoads={filteredLoads}
          installationsMap={installationsMap}
          isLoading={isLoading}
          hasActiveFilters={hasActiveFilters}
          expandedLoads={expandedLoads}
          expandedInstallations={expandedInstallations}
          showMapForLoad={showMapForLoad}
          highlightedTaskId={highlightedTaskId}
          onToggleExpand={toggleExpand}
          onToggleMap={toggleMapForLoad}
          onToggleInstallationExpand={toggleInstallationExpand}
          onUpdateLoadStatus={updateLoadStatus}
          onInstallationStatusChange={handleInstallationStatusChange}
          onTaskClick={handleTaskClick}
          onPrint={handlePrintLoad}
          onClearFilters={handleClearFilters}
        />

        <LoadStatusDialog
          dialog={statusDialog}
          statusReason={statusReason}
          onStatusReasonChange={setStatusReason}
          onClose={handleStatusDialogClose}
          onConfirm={handleStatusDialogConfirm}
        />
      </div>
    </Layout>
  );
}
